import { c as _c } from "react/compiler-runtime";
import { AccommodationCard, AccommodationCardSkeleton } from "@website/accommodation-ui/accommodation-card";
import { HotelCitiesMainContentPropertiesType } from "@website/accommodation-ui/hotel-cities-main-content";
import { ListingContainerPropertiesType } from "@website/accommodation-ui/listing-container";
import { useFetcher, useFetchers } from "@website/fetcher/use-fetcher";
import { useLoginStatus } from "@website/hooks/auth-manager";
import { Button } from "@website/ui/button";
import { useSnackbar } from "@website/ui/snackbar";
import { getOrigin } from "@website/utils/channel/get-origin";
import { isInvalidCheckin, today, tomorrow } from "@website/utils/date-format";
import { ExperimentSDK } from "@website/utils/experiments/client";
import { isFilled } from "@website/utils/is-filled";
import { MapFilterProperties, useCityFilterState, useFilters } from "@website/widget/filter";
import { HeaderPropertiesType } from "@website/widget/header";
import { TelesalesAppName } from "@website/widget/telesales";
import isEmpty from "lodash-es/isEmpty";
import last from "lodash-es/last";
import omit from "lodash-es/omit";
import orderBy from "lodash-es/orderBy";
import pick from "lodash-es/pick";
import toNumber from "lodash-es/toNumber";
import toString from "lodash-es/toString";
import uniq from "lodash-es/uniq";
import { useRouter } from "next/router";
import { useEffect, useRef, useState } from "react";
import { useVillagesFilter } from "./filter.hook";
import { VILLA_LIST_PAGE_SIZE, fetchHotelRateData, fetchHotelReviewData, fetchInstalmentWallet, fetchVillagesData, transformHotelSchema, transformVillaListingSchema } from "./index.server";
import { handleEventMapSearchClicked, handleVillaAccommodationCardTrackingEvents, handleVillaCityPageVisited } from "./index.tracking";
import { CityPagePropertiesType, HotelCityContentItemType, VillaListingCardType, VillaLocationResponseType, reasonsSchema } from "./index.types";
import { useVillaSearch } from "./search.hook";
import { VILLA_LOCATION_ROUTE_QUERY_KEYS, applyVillaSeoCategoryDefault } from "./seo-category";
export const getBannersByType = (data, type: string) => data?.find?.(item => item?.type === type)?.data?.sort?.((a, b) => a?.order - b?.order) || null || [];
export const useCityPage = properties => {
  const $ = _c(246);
  const {
    locationData,
    villagesData,
    seoCategory,
    hasError,
    smallBanners,
    staticBanners
  } = properties;
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = [1];
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [pages, setPages] = useState(t0);
  const router = useRouter();
  const [updatedModal, setUpdatedModal] = useState(false);
  const timerReference = useRef(null);
  const t1 = properties?.seoData?.pageTitle;
  let t2;
  if ($[1] !== locationData || $[2] !== t1) {
    t2 = {
      locationData,
      seoPageTitle: t1
    };
    $[1] = locationData;
    $[2] = t1;
    $[3] = t2;
  } else {
    t2 = $[3];
  }
  const searchProperties = useVillaSearch(t2);
  const {
    onOpenFilters
  } = useCityFilterState();
  const {
    isLoggedIn
  } = useLoginStatus();
  const selectedHotelId = router.query?.selectedHotelId;
  const {
    enqueue,
    dequeue
  } = useSnackbar();
  const isCompleteInfoDismissed = useRef(false);
  const effectiveListingQuery = applyVillaSeoCategoryDefault(router.query, seoCategory);
  let t3;
  if ($[4] === Symbol.for("react.memo_cache_sentinel")) {
    t3 = ["instalmentWallet"];
    $[4] = t3;
  } else {
    t3 = $[4];
  }
  let t4;
  if ($[5] !== isLoggedIn) {
    t4 = {
      enabled: isLoggedIn,
      retry: false,
      queryKey: t3,
      queryFn: _temp
    };
    $[5] = isLoggedIn;
    $[6] = t4;
  } else {
    t4 = $[6];
  }
  const {
    data: instalmentWallet,
    isLoading: isInstallmentWalletLoading,
    isFetching: isInstallmentWalletFetching
  } = useFetcher(t4);
  const t5 = pages.map(page => ({
    queryKey: ["villagesData", omit(effectiveListingQuery, ["modal", "selectedHotelId", "popover", "item", "filters_visible"]), page],
    queryFn: () => fetchVillagesData({
      ...router.query,
      page
    }, seoCategory)
  }));
  let t6;
  if ($[7] !== t5) {
    t6 = {
      queries: t5
    };
    $[7] = t5;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  const freshVillagesData = useFetchers(t6);
  let t7;
  if ($[9] !== freshVillagesData) {
    t7 = () => Promise.any(freshVillagesData.map(_temp2)).finally(() => setUpdatedModal(false));
    $[9] = freshVillagesData;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  const updateResultSearchHandler = t7;
  let t8;
  if ($[11] !== router) {
    t8 = value => router.push({
      pathname: router.pathname,
      query: {
        ...router.query,
        order_by: value
      }
    }, undefined, {
      shallow: true,
      scroll: false
    });
    $[11] = router;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  const onSortChange = t8;
  let t10;
  let t9;
  if ($[13] === Symbol.for("react.memo_cache_sentinel")) {
    t9 = () => {
      timerReference.current = setTimeout(() => setUpdatedModal(true), 900000);
      return () => {
        timerReference.current && clearTimeout(timerReference.current);
      };
    };
    t10 = [];
    $[13] = t10;
    $[14] = t9;
  } else {
    t10 = $[13];
    t9 = $[14];
  }
  useEffect(t9, t10);
  let t11;
  if ($[15] !== router) {
    t11 = () => {
      if (!router.isReady) {
        return;
      }
      const {
        date_from,
        date_to
      } = router.query;
      const shouldRedirect = isInvalidCheckin(date_from, date_to);
      if (shouldRedirect) {
        router.replace({
          pathname: router.pathname,
          query: pick(router.query, VILLA_LOCATION_ROUTE_QUERY_KEYS)
        }, undefined, {
          shallow: true,
          scroll: false
        });
      }
    };
    $[15] = router;
    $[16] = t11;
  } else {
    t11 = $[16];
  }
  let t12;
  if ($[17] !== router.isReady || $[18] !== router.query) {
    t12 = [router.isReady, router.query];
    $[17] = router.isReady;
    $[18] = router.query;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  useEffect(t11, t12);
  let t13;
  if ($[20] !== freshVillagesData) {
    t13 = !freshVillagesData?.length || freshVillagesData?.some(_temp3);
    $[20] = freshVillagesData;
    $[21] = t13;
  } else {
    t13 = $[21];
  }
  const loading = t13;
  const villageResponses = freshVillagesData?.flatMap(_temp4) || [];
  let t14;
  if ($[22] !== router.query) {
    t14 = isEmpty(omit(router.query, VILLA_LOCATION_ROUTE_QUERY_KEYS));
    $[22] = router.query;
    $[23] = t14;
  } else {
    t14 = $[23];
  }
  const hasOnlyLocationQuery = t14;
  let t15;
  if ($[24] !== hasOnlyLocationQuery) {
    t15 = hasOnlyLocationQuery || ExperimentSDK.isOn("new-villa-plp");
    $[24] = hasOnlyLocationQuery;
    $[25] = t15;
  } else {
    t15 = $[25];
  }
  const isNewPlP = t15;
  const activeVillageResponses = hasOnlyLocationQuery && villageResponses.length === 0 ? villagesData ? [villagesData] : [] : villageResponses;
  const filteredVillages = activeVillageResponses.flatMap(_temp5);
  let t16;
  if ($[26] !== router.query) {
    t16 = response => ((response?.data || []) as Array<VillaLocationResponseType>).map(item => ({
      ...transformHotelSchema(item, router.query),
      count: toNumber(response.count || 0),
      total: toNumber(response.total || 0),
      page: toNumber(response.current_page || 1)
    }));
    $[26] = router.query;
    $[27] = t16;
  } else {
    t16 = $[27];
  }
  const hotelCitiesMainContentList = activeVillageResponses.flatMap(t16);
  let t17;
  if ($[28] !== router.query.date_from || $[29] !== router.query.date_to || $[30] !== router.query.room_capacity) {
    t17 = (item_0, index) => transformVillaListingSchema(item_0, index, {
      ...(router.query?.date_from ? {
        date_from: toString(router.query.date_from)
      } : {}),
      ...(router.query?.date_to ? {
        date_to: toString(router.query.date_to)
      } : {}),
      ...(toNumber(router.query?.room_capacity) > 0 ? {
        room_capacity: toNumber(router.query.room_capacity)
      } : {})
    });
    $[28] = router.query.date_from;
    $[29] = router.query.date_to;
    $[30] = router.query.room_capacity;
    $[31] = t17;
  } else {
    t17 = $[31];
  }
  const transformedVillages = filteredVillages.map(t17);
  const totalVillagesCount = last(activeVillageResponses)?.total || 0;
  const citiesFilterProperties = useVillagesFilter(totalVillagesCount, freshVillagesData?.[0]?.isFetched, locationData?.title, isNewPlP);
  let t18;
  if ($[32] !== citiesFilterProperties.filtersSchema) {
    t18 = {
      schema: citiesFilterProperties.filtersSchema
    };
    $[32] = citiesFilterProperties.filtersSchema;
    $[33] = t18;
  } else {
    t18 = $[33];
  }
  const {
    selectedFiltersCount
  } = useFilters(t18);
  let t19;
  if ($[34] !== locationData) {
    t19 = locationData ? {
      id: locationData.id,
      title: locationData.title
    } : undefined;
    $[34] = locationData;
    $[35] = t19;
  } else {
    t19 = $[35];
  }
  const trackingLocation = t19;
  const hasSelectedFilters = selectedFiltersCount !== 0;
  let t20;
  if ($[36] !== hasSelectedFilters || $[37] !== locationData?.title || $[38] !== router.query.locationType) {
    t20 = hasSelectedFilters ? <>متاسفانه در حال حاضر هتل و اقامتگاهی متناسب با فیلتر های انتخابی شما در این بازه تاریخی وجود ندارد .</> : router.query.locationType === "category" ? <>متاسفانه در حال حاضر اقامتگاهی متناسب با دسته‌بندی انتخابی شما یافت نشد.</> : <>متاسفانه هتل و اقامتگاه فعالی در این بازه تاریخی در شهر{" "}<span className="text-on-surface-high-emphasis font-medium">"{locationData?.title}"</span>{" "}یافت نشد. لطفا شهرهای دیگر را جستجو کنید.</>;
    $[36] = hasSelectedFilters;
    $[37] = locationData?.title;
    $[38] = router.query.locationType;
    $[39] = t20;
  } else {
    t20 = $[39];
  }
  const listingEmptyStateCaption = t20;
  let t21;
  if ($[40] !== citiesFilterProperties || $[41] !== hasSelectedFilters || $[42] !== searchProperties) {
    t21 = () => {
      if (hasSelectedFilters) {
        citiesFilterProperties.clearAllFilters();
        return;
      }
      searchProperties.setIsSearchPanelOpen(true);
    };
    $[40] = citiesFilterProperties;
    $[41] = hasSelectedFilters;
    $[42] = searchProperties;
    $[43] = t21;
  } else {
    t21 = $[43];
  }
  const handleListingEmptyStateAction = t21;
  const filteredPages = orderBy(uniq(activeVillageResponses.map(_temp6).filter(Boolean)));
  const [cityPageVisitFirstVilla] = transformedVillages;
  const cityPageVisitCity = trackingLocation?.title ?? cityPageVisitFirstVilla?.cityTitle ?? "";
  const cityPageVisitCityId = trackingLocation?.id ?? cityPageVisitFirstVilla?.cityId ?? 0;
  const cityPageVisitAvailableHotelIds = filteredVillages.filter(_temp7).map(_temp8).filter(Boolean).join(",");
  const cityPageVisitPage = last(filteredPages) || 0;
  let t22;
  if ($[44] !== router.query.locationType) {
    t22 = toString(router.query.locationType);
    $[44] = router.query.locationType;
    $[45] = t22;
  } else {
    t22 = $[45];
  }
  const cityPageVisitPageType = t22;
  let t23;
  if ($[46] !== router.query.date_from) {
    t23 = toString(router.query.date_from);
    $[46] = router.query.date_from;
    $[47] = t23;
  } else {
    t23 = $[47];
  }
  const cityPageVisitDateFrom = t23;
  let t24;
  if ($[48] !== router.query.date_to) {
    t24 = toString(router.query.date_to);
    $[48] = router.query.date_to;
    $[49] = t24;
  } else {
    t24 = $[49];
  }
  const cityPageVisitDateTo = t24;
  let t25;
  if ($[50] !== cityPageVisitAvailableHotelIds || $[51] !== cityPageVisitCity || $[52] !== cityPageVisitCityId || $[53] !== cityPageVisitDateFrom || $[54] !== cityPageVisitDateTo || $[55] !== cityPageVisitPage || $[56] !== cityPageVisitPageType) {
    t25 = [cityPageVisitCity, cityPageVisitCityId, cityPageVisitDateFrom, cityPageVisitDateTo, cityPageVisitAvailableHotelIds, cityPageVisitPage, cityPageVisitPageType];
    $[50] = cityPageVisitAvailableHotelIds;
    $[51] = cityPageVisitCity;
    $[52] = cityPageVisitCityId;
    $[53] = cityPageVisitDateFrom;
    $[54] = cityPageVisitDateTo;
    $[55] = cityPageVisitPage;
    $[56] = cityPageVisitPageType;
    $[57] = t25;
  } else {
    t25 = $[57];
  }
  const cityPageVisitTrackingKey = t25.join("|");
  const lastCityPageVisitTrackingKey = useRef(null);
  let t26;
  let t27;
  if ($[58] !== cityPageVisitAvailableHotelIds || $[59] !== cityPageVisitCity || $[60] !== cityPageVisitCityId || $[61] !== cityPageVisitDateFrom || $[62] !== cityPageVisitDateTo || $[63] !== cityPageVisitPage || $[64] !== cityPageVisitPageType || $[65] !== cityPageVisitTrackingKey || $[66] !== isNewPlP || $[67] !== loading) {
    t26 = () => {
      if (!isNewPlP || loading || !cityPageVisitCityId || lastCityPageVisitTrackingKey.current === cityPageVisitTrackingKey) {
        return;
      }
      lastCityPageVisitTrackingKey.current = cityPageVisitTrackingKey;
      handleVillaCityPageVisited({
        city: cityPageVisitCity,
        city_id: cityPageVisitCityId,
        date_from: new Date(cityPageVisitDateFrom),
        date_to: new Date(cityPageVisitDateTo),
        available_hotels_id: cityPageVisitAvailableHotelIds.split(",").filter(Boolean).map(Number),
        page: cityPageVisitPage,
        last_minute_loaded: 0,
        count_of_last_minute_cards: 0,
        blue_box_loaded: 0,
        count_of_blue_box_cards: 0,
        page_type: cityPageVisitPageType
      });
    };
    t27 = [cityPageVisitAvailableHotelIds, cityPageVisitCity, cityPageVisitCityId, cityPageVisitDateFrom, cityPageVisitDateTo, cityPageVisitPage, cityPageVisitPageType, cityPageVisitTrackingKey, isNewPlP, loading];
    $[58] = cityPageVisitAvailableHotelIds;
    $[59] = cityPageVisitCity;
    $[60] = cityPageVisitCityId;
    $[61] = cityPageVisitDateFrom;
    $[62] = cityPageVisitDateTo;
    $[63] = cityPageVisitPage;
    $[64] = cityPageVisitPageType;
    $[65] = cityPageVisitTrackingKey;
    $[66] = isNewPlP;
    $[67] = loading;
    $[68] = t26;
    $[69] = t27;
  } else {
    t26 = $[68];
    t27 = $[69];
  }
  useEffect(t26, t27);
  let t28;
  if ($[70] !== locationData?.location) {
    t28 = locationData?.location || {};
    $[70] = locationData?.location;
    $[71] = t28;
  } else {
    t28 = $[71];
  }
  const {
    latitude,
    longitude
  } = t28;
  const coordinate = latitude && longitude ? `${latitude},${longitude},11,33` : null;
  let t29;
  if ($[72] !== coordinate || $[73] !== effectiveListingQuery || $[74] !== locationData?.id || $[75] !== locationData?.title || $[76] !== router) {
    const goToMapFilter = () => {
      handleEventMapSearchClicked({
        city: locationData?.title,
        city_id: locationData?.id,
        date_from: toString(router.query?.date_from),
        date_to: toString(router.query?.date_to)
      });
      return router.push({
        pathname: "/map",
        query: {
          ...(coordinate ? {
            coordinate
          } : {}),
          ...omit(effectiveListingQuery, [...VILLA_LOCATION_ROUTE_QUERY_KEYS, "viewport_change_type"])
        }
      });
    };
    t29 = {
      label: "\u0645\u0634\u0627\u0647\u062F\u0647 \u0631\u0648\u06CC \u0646\u0642\u0634\u0647",
      onClick: goToMapFilter
    };
    $[72] = coordinate;
    $[73] = effectiveListingQuery;
    $[74] = locationData?.id;
    $[75] = locationData?.title;
    $[76] = router;
    $[77] = t29;
  } else {
    t29 = $[77];
  }
  const mapFilterProperties = t29;
  const t30 = Boolean(selectedHotelId);
  let t31;
  if ($[78] !== selectedHotelId) {
    t31 = ["hotelRateData", selectedHotelId];
    $[78] = selectedHotelId;
    $[79] = t31;
  } else {
    t31 = $[79];
  }
  let t32;
  if ($[80] !== t30 || $[81] !== t31) {
    t32 = {
      refetchOnMount: true,
      enabled: t30,
      queryKey: t31,
      queryFn: _temp9
    };
    $[80] = t30;
    $[81] = t31;
    $[82] = t32;
  } else {
    t32 = $[82];
  }
  const {
    data: hotelRateData
  } = useFetcher(t32);
  const t33 = Boolean(selectedHotelId);
  let t34;
  if ($[83] !== selectedHotelId) {
    t34 = ["hotelReviewData", selectedHotelId];
    $[83] = selectedHotelId;
    $[84] = t34;
  } else {
    t34 = $[84];
  }
  let t35;
  if ($[85] !== t33 || $[86] !== t34) {
    t35 = {
      refetchOnMount: true,
      enabled: t33,
      queryKey: t34,
      queryFn: _temp10
    };
    $[85] = t33;
    $[86] = t34;
    $[87] = t35;
  } else {
    t35 = $[87];
  }
  const {
    data: hotelReviewData
  } = useFetcher(t35);
  let t36;
  if ($[88] !== coordinate || $[89] !== effectiveListingQuery || $[90] !== locationData?.id || $[91] !== locationData?.title || $[92] !== router) {
    t36 = () => {
      handleEventMapSearchClicked({
        city: locationData?.title,
        city_id: locationData?.id,
        date_from: toString(router.query?.date_from),
        date_to: toString(router.query?.date_to)
      });
      router?.push({
        pathname: "/map",
        query: {
          ...(coordinate ? {
            coordinate
          } : {}),
          ...omit(effectiveListingQuery, [...VILLA_LOCATION_ROUTE_QUERY_KEYS, "extra_capacity", "modal", "coordinate", "viewport_change_type"])
        }
      }, undefined, {
        shallow: true,
        scroll: false
      });
    };
    $[88] = coordinate;
    $[89] = effectiveListingQuery;
    $[90] = locationData?.id;
    $[91] = locationData?.title;
    $[92] = router;
    $[93] = t36;
  } else {
    t36 = $[93];
  }
  const onMapClick = t36;
  let t37;
  if ($[94] !== dequeue || $[95] !== enqueue || $[96] !== router.query || $[97] !== searchProperties.setIsSearchPanelOpen) {
    t37 = () => {
      if (["date_from", "date_to"].every(key => !isFilled(router.query[key]))) {
        if (isCompleteInfoDismissed.current) {
          return;
        }
        const buttonProperties = {
          onClick: () => {
            searchProperties.setIsSearchPanelOpen.bind(null, true)();
            setTimeout(() => {
              dequeue(snackBarId);
            }, 100);
          },
          color: "secondary" as const,
          variant: "text" as const
        };
        const snackBarId = enqueue("\u0628\u0631\u0627\u06CC \u0645\u0634\u0627\u0647\u062F\u0647 \u0642\u06CC\u0645\u062A \u062F\u0642\u06CC\u0642 \u0627\u0642\u0627\u0645\u062A\u06AF\u0627\u0647\u200C\u0647\u0627\u066C \u062A\u0627\u0631\u06CC\u062E \u0631\u0627 \u0648\u0627\u0631\u062F \u06A9\u0646\u06CC\u062F.", "default", "one-line", <Button {...buttonProperties}>تکمیل اطلاعات</Button>);
        isCompleteInfoDismissed.current = true;
      }
    };
    $[94] = dequeue;
    $[95] = enqueue;
    $[96] = router.query;
    $[97] = searchProperties.setIsSearchPanelOpen;
    $[98] = t37;
  } else {
    t37 = $[98];
  }
  let t38;
  if ($[99] !== dequeue || $[100] !== enqueue || $[101] !== router || $[102] !== searchProperties.setIsSearchPanelOpen) {
    t38 = [router, searchProperties.setIsSearchPanelOpen, enqueue, dequeue];
    $[99] = dequeue;
    $[100] = enqueue;
    $[101] = router;
    $[102] = searchProperties.setIsSearchPanelOpen;
    $[103] = t38;
  } else {
    t38 = $[103];
  }
  useEffect(t37, t38);
  let t39;
  if ($[104] === Symbol.for("react.memo_cache_sentinel")) {
    t39 = {
      title: "\u0627\u0642\u0627\u0645\u062A\u06AF\u0627\u0647"
    };
    $[104] = t39;
  } else {
    t39 = $[104];
  }
  const t40 = coordinate && mapFilterProperties;
  let t41;
  if ($[105] !== citiesFilterProperties || $[106] !== loading || $[107] !== t40) {
    t41 = {
      ...citiesFilterProperties,
      mapFilterProps: t40,
      loading
    };
    $[105] = citiesFilterProperties;
    $[106] = loading;
    $[107] = t40;
    $[108] = t41;
  } else {
    t41 = $[108];
  }
  let t42;
  if ($[109] !== updateResultSearchHandler || $[110] !== updatedModal) {
    t42 = {
      isOpen: updatedModal,
      onUpdateResult: updateResultSearchHandler
    };
    $[109] = updateResultSearchHandler;
    $[110] = updatedModal;
    $[111] = t42;
  } else {
    t42 = $[111];
  }
  const t43 = hasOnlyLocationQuery ? VILLA_LIST_PAGE_SIZE : 0;
  let t44;
  if ($[112] !== staticBanners) {
    t44 = getBannersByType(staticBanners, "static");
    $[112] = staticBanners;
    $[113] = t44;
  } else {
    t44 = $[113];
  }
  let t45;
  if ($[114] !== t44) {
    t45 = {
      slide: t44
    };
    $[114] = t44;
    $[115] = t45;
  } else {
    t45 = $[115];
  }
  const t46 = hasSelectedFilters ? "\u062D\u0630\u0641 \u0641\u06CC\u0644\u062A\u0631\u0647\u0627" : "\u062A\u063A\u06CC\u06CC\u0631 \u0645\u0642\u0635\u062F";
  const t47 = hasSelectedFilters ? "filters-not-found" : "not-found";
  let t48;
  if ($[116] !== handleListingEmptyStateAction || $[117] !== listingEmptyStateCaption || $[118] !== t46 || $[119] !== t47) {
    t48 = {
      actionTitle: t46,
      caption: listingEmptyStateCaption,
      onActionClick: handleListingEmptyStateAction,
      variant: t47
    };
    $[116] = handleListingEmptyStateAction;
    $[117] = listingEmptyStateCaption;
    $[118] = t46;
    $[119] = t47;
    $[120] = t48;
  } else {
    t48 = $[120];
  }
  let t49;
  if ($[121] !== router.query) {
    t49 = item_2 => <AccommodationCard {...item_2} handleTrackingEvents={trackingData => handleVillaAccommodationCardTrackingEvents({
      ...item_2,
      ...trackingData
    }, router.query)} />;
    $[121] = router.query;
    $[122] = t49;
  } else {
    t49 = $[122];
  }
  let t50;
  if ($[123] !== loading || $[124] !== t43 || $[125] !== t45 || $[126] !== t48 || $[127] !== t49 || $[128] !== transformedVillages) {
    t50 = {
      list: transformedVillages,
      isLoading: loading,
      animationStartIndex: t43,
      renderSkeleton: _temp11,
      getKey: _temp12,
      banners: t45,
      emptyState: t48,
      renderCard: t49
    };
    $[123] = loading;
    $[124] = t43;
    $[125] = t45;
    $[126] = t48;
    $[127] = t49;
    $[128] = transformedVillages;
    $[129] = t50;
  } else {
    t50 = $[129];
  }
  const t51 = t50 satisfies ListingContainerPropertiesType<VillaListingCardType>;
  const t52 = citiesFilterProperties.chipsFiltersSchema;
  const t53 = citiesFilterProperties.clearAllFilters;
  const t54 = citiesFilterProperties?.isFilterFetching;
  const t55 = locationData?.title;
  const t56 = router.query.locationType !== "category";
  const t57 = citiesFilterProperties?.hasNearby;
  const t58 = citiesFilterProperties.showStripLoadingSkeleton;
  const t59 = !hasOnlyLocationQuery;
  let t60;
  if ($[130] !== router.query.order_by) {
    t60 = toString(router.query.order_by);
    $[130] = router.query.order_by;
    $[131] = t60;
  } else {
    t60 = $[131];
  }
  let t61;
  if ($[132] === Symbol.for("react.memo_cache_sentinel")) {
    t61 = [{
      label: "\u0627\u0631\u0632\u0627\u0646\u062A\u0631\u06CC\u0646",
      value: "asc"
    }, {
      label: "\u06AF\u0631\u0627\u0646\u062A\u0631\u06CC\u0646",
      value: "desc"
    }];
    $[132] = t61;
  } else {
    t61 = $[132];
  }
  let t62;
  if ($[133] !== isNewPlP || $[134] !== onSortChange || $[135] !== t60) {
    t62 = {
      isNewPlP,
      onChange: onSortChange,
      initialValue: t60,
      list: t61
    };
    $[133] = isNewPlP;
    $[134] = onSortChange;
    $[135] = t60;
    $[136] = t62;
  } else {
    t62 = $[136];
  }
  let t63;
  if ($[137] !== citiesFilterProperties.chipsFiltersSchema || $[138] !== citiesFilterProperties.clearAllFilters || $[139] !== citiesFilterProperties.showStripLoadingSkeleton || $[140] !== isNewPlP || $[141] !== loading || $[142] !== onOpenFilters || $[143] !== selectedFiltersCount || $[144] !== t54 || $[145] !== t55 || $[146] !== t56 || $[147] !== t57 || $[148] !== t59 || $[149] !== t62 || $[150] !== totalVillagesCount) {
    t63 = {
      chipsFiltersSchema: t52,
      clearAllFilters: t53,
      loading,
      isFilterFetching: t54,
      setFilterOpen: onOpenFilters,
      city: t55,
      totalItem: totalVillagesCount,
      hasCityPrefix: t56,
      hasCityNearby: t57,
      selectedFiltersCount,
      showStripLoadingSkeleton: t58,
      shouldAnimateStripItems: t59,
      isNewPlP,
      sortProperties: t62
    };
    $[137] = citiesFilterProperties.chipsFiltersSchema;
    $[138] = citiesFilterProperties.clearAllFilters;
    $[139] = citiesFilterProperties.showStripLoadingSkeleton;
    $[140] = isNewPlP;
    $[141] = loading;
    $[142] = onOpenFilters;
    $[143] = selectedFiltersCount;
    $[144] = t54;
    $[145] = t55;
    $[146] = t56;
    $[147] = t57;
    $[148] = t59;
    $[149] = t62;
    $[150] = totalVillagesCount;
    $[151] = t63;
  } else {
    t63 = $[151];
  }
  let t64;
  if ($[152] !== filteredPages || $[153] !== loading || $[154] !== totalVillagesCount) {
    t64 = {
      pages: filteredPages,
      setPages,
      loading,
      totalCount: totalVillagesCount,
      pageSize: VILLA_LIST_PAGE_SIZE
    };
    $[152] = filteredPages;
    $[153] = loading;
    $[154] = totalVillagesCount;
    $[155] = t64;
  } else {
    t64 = $[155];
  }
  const t65 = locationData?.title;
  const t66 = citiesFilterProperties.clearAllFilters;
  const t67 = citiesFilterProperties?.hasNearby;
  const t68 = Number(selectedHotelId);
  let t69;
  if ($[156] !== hotelCitiesMainContentList) {
    t69 = hotelCitiesMainContentList || [];
    $[156] = hotelCitiesMainContentList;
    $[157] = t69;
  } else {
    t69 = $[157];
  }
  let t70;
  if ($[158] !== hotelReviewData?.rateReviews) {
    t70 = hotelReviewData?.rateReviews?.map(_temp13)?.splice(0, 5);
    $[158] = hotelReviewData?.rateReviews;
    $[159] = t70;
  } else {
    t70 = $[159];
  }
  let t71;
  if ($[160] !== hotelRateData || $[161] !== t70) {
    t71 = {
      rate: hotelRateData,
      comments: t70
    };
    $[160] = hotelRateData;
    $[161] = t70;
    $[162] = t71;
  } else {
    t71 = $[162];
  }
  const t72 = t71 as unknown as HotelCitiesMainContentPropertiesType["reviewData"];
  let t73;
  if ($[163] === Symbol.for("react.memo_cache_sentinel")) {
    t73 = [];
    $[163] = t73;
  } else {
    t73 = $[163];
  }
  const t74 = searchProperties.setIsSearchPanelOpen;
  let t75;
  if ($[164] !== staticBanners) {
    t75 = getBannersByType(staticBanners, "static");
    $[164] = staticBanners;
    $[165] = t75;
  } else {
    t75 = $[165];
  }
  const t76 = instalmentWallet?.isEligible;
  const t77 = instalmentWallet?.showDownPayment;
  const t78 = instalmentWallet?.markup;
  const t79 = isInstallmentWalletLoading || isInstallmentWalletFetching;
  let t80;
  if ($[166] !== t76 || $[167] !== t77 || $[168] !== t78 || $[169] !== t79) {
    t80 = {
      isEligible: t76,
      showDownPayment: t77,
      markup: t78,
      isLoading: t79
    };
    $[166] = t76;
    $[167] = t77;
    $[168] = t78;
    $[169] = t79;
    $[170] = t80;
  } else {
    t80 = $[170];
  }
  let t81;
  if ($[171] !== citiesFilterProperties.clearAllFilters || $[172] !== hasError || $[173] !== hasSelectedFilters || $[174] !== isNewPlP || $[175] !== loading || $[176] !== searchProperties.setIsSearchPanelOpen || $[177] !== t65 || $[178] !== t67 || $[179] !== t68 || $[180] !== t69 || $[181] !== t72 || $[182] !== t75 || $[183] !== t80 || $[184] !== totalVillagesCount || $[185] !== trackingLocation) {
    t81 = {
      size: "small" as const,
      cityTitle: t65,
      clearAllFilters: t66,
      hasError,
      hasFilters: hasSelectedFilters,
      hasCityNearby: t67,
      selectedHotelId: t68,
      hotelList: t69,
      reviewData: t72,
      hotelsSuggested: t73,
      loading,
      setIsSearchPanelOpen: t74,
      bannerSlideList: t75,
      totalItem: totalVillagesCount,
      snappPayData: t80,
      trackingLocation,
      isNewPlP
    };
    $[171] = citiesFilterProperties.clearAllFilters;
    $[172] = hasError;
    $[173] = hasSelectedFilters;
    $[174] = isNewPlP;
    $[175] = loading;
    $[176] = searchProperties.setIsSearchPanelOpen;
    $[177] = t65;
    $[178] = t67;
    $[179] = t68;
    $[180] = t69;
    $[181] = t72;
    $[182] = t75;
    $[183] = t80;
    $[184] = totalVillagesCount;
    $[185] = trackingLocation;
    $[186] = t81;
  } else {
    t81 = $[186];
  }
  let t82;
  if ($[187] === Symbol.for("react.memo_cache_sentinel")) {
    t82 = getOrigin();
    $[187] = t82;
  } else {
    t82 = $[187];
  }
  let t83;
  if ($[188] !== isNewPlP) {
    t83 = {
      title: "\u0648\u06CC\u0644\u0627 \u0648 \u0627\u0642\u0627\u0645\u062A\u06AF\u0627\u0647",
      href: `${t82}/villa`,
      ...(isNewPlP ? {
        className: "!text-xs !leading-4"
      } : {})
    };
    $[188] = isNewPlP;
    $[189] = t83;
  } else {
    t83 = $[189];
  }
  let t84;
  if ($[190] !== isNewPlP || $[191] !== locationData?.state?.slug || $[192] !== locationData?.state?.title || $[193] !== router.query) {
    t84 = locationData?.state?.slug ? [{
      title: `ویلاهای استان ${locationData?.state?.title}`,
      ...(isNewPlP ? {
        className: "!text-xs !leading-4"
      } : {}),
      href: {
        pathname: "/[locationType]/[cityTitle]",
        query: {
          ...pick(router.query, ["cityTitle", "locationType"]),
          locationType: "state",
          cityTitle: locationData?.state?.slug
        }
      }
    }] : [];
    $[190] = isNewPlP;
    $[191] = locationData?.state?.slug;
    $[192] = locationData?.state?.title;
    $[193] = router.query;
    $[194] = t84;
  } else {
    t84 = $[194];
  }
  const t85 = router.query.locationType === "category" ? locationData?.title : ` ویلاهای ${locationData?.title}`;
  let t86;
  if ($[195] !== isNewPlP) {
    t86 = isNewPlP ? {
      className: "!text-xs !leading-4"
    } : {};
    $[195] = isNewPlP;
    $[196] = t86;
  } else {
    t86 = $[196];
  }
  let t87;
  if ($[197] !== router.query) {
    t87 = pick(router.query, ["cityTitle", "locationType"]);
    $[197] = router.query;
    $[198] = t87;
  } else {
    t87 = $[198];
  }
  let t88;
  if ($[199] !== t87) {
    t88 = {
      pathname: "/[locationType]/[cityTitle]",
      query: t87
    };
    $[199] = t87;
    $[200] = t88;
  } else {
    t88 = $[200];
  }
  let t89;
  if ($[201] !== t85 || $[202] !== t86 || $[203] !== t88) {
    t89 = {
      title: t85,
      ...t86,
      href: t88
    };
    $[201] = t85;
    $[202] = t86;
    $[203] = t88;
    $[204] = t89;
  } else {
    t89 = $[204];
  }
  let t90;
  if ($[205] !== t83 || $[206] !== t84 || $[207] !== t89) {
    t90 = {
      color: "default" as const,
      variant: "default" as const,
      list: [t83, ...t84, t89]
    };
    $[205] = t83;
    $[206] = t84;
    $[207] = t89;
    $[208] = t90;
  } else {
    t90 = $[208];
  }
  let t91;
  if ($[209] !== router.query.date_from) {
    t91 = router?.query?.date_from || today();
    $[209] = router.query.date_from;
    $[210] = t91;
  } else {
    t91 = $[210];
  }
  let t92;
  if ($[211] !== t91) {
    t92 = toString(t91);
    $[211] = t91;
    $[212] = t92;
  } else {
    t92 = $[212];
  }
  let t93;
  if ($[213] !== router.query.date_to) {
    t93 = router?.query?.date_to || tomorrow();
    $[213] = router.query.date_to;
    $[214] = t93;
  } else {
    t93 = $[214];
  }
  let t94;
  if ($[215] !== t93) {
    t94 = toString(t93);
    $[215] = t93;
    $[216] = t94;
  } else {
    t94 = $[216];
  }
  const t95 = router?.query?.cityTitle;
  let t96;
  if ($[217] !== t95) {
    t96 = toString(t95);
    $[217] = t95;
    $[218] = t96;
  } else {
    t96 = $[218];
  }
  let t97;
  if ($[219] !== t92 || $[220] !== t94 || $[221] !== t96) {
    t97 = {
      app: "villa" as TelesalesAppName,
      checkin: t92,
      checkout: t94,
      city_from: "",
      city_to: t96,
      funnel: "telesales_box"
    };
    $[219] = t92;
    $[220] = t94;
    $[221] = t96;
    $[222] = t97;
  } else {
    t97 = $[222];
  }
  const t98 = !isNewPlP;
  const t99 = isNewPlP ? "scroll" as const : undefined;
  let t100;
  if ($[223] !== isNewPlP || $[224] !== t97 || $[225] !== t98 || $[226] !== t99) {
    t100 = {
      description: " \u0628\u0639\u062F \u0627\u0632 \u062B\u0628\u062A \u062F\u0631\u062E\u0648\u0627\u0633\u062A\u060C \u06A9\u0627\u0631\u0634\u0646\u0627\u0633\u0627\u0646 \u0645\u0627 \u062F\u0631 \u0627\u0633\u0631\u0639 \u0648\u0642\u062A \u0628\u0627 \u0634\u0645\u0627 \u062A\u0645\u0627\u0633 \u062E\u0648\u0627\u0647\u0646\u062F \u06AF\u0631\u0641\u062A \u0648 \u0634\u0645\u0627 \u0631\u0627 \u0628\u0631\u0627\u06CC \u0631\u0632\u0631\u0648 \u0648\u06CC\u0644\u0627 \u0631\u0627\u0647\u0646\u0645\u0627\u06CC\u06CC \u062E\u0648\u0627\u0647\u0646\u062F \u06A9\u0631\u062F.",
      reasons: reasonsSchema,
      requestData: t97,
      storageKey: "villa_submit-tele-sale-reserve",
      className: "!left-6 !right-auto",
      showAnimatedMobileButton: t98,
      mobileButtonAnimation: t99,
      isNewPlP
    };
    $[223] = isNewPlP;
    $[224] = t97;
    $[225] = t98;
    $[226] = t99;
    $[227] = t100;
  } else {
    t100 = $[227];
  }
  let t101;
  if ($[228] !== smallBanners) {
    t101 = getBannersByType(smallBanners, "small");
    $[228] = smallBanners;
    $[229] = t101;
  } else {
    t101 = $[229];
  }
  let t102;
  if ($[230] !== t101) {
    t102 = {
      bannerSlideList: t101
    };
    $[230] = t101;
    $[231] = t102;
  } else {
    t102 = $[231];
  }
  const t103 = Boolean(coordinate);
  let t104;
  if ($[232] !== isNewPlP || $[233] !== onMapClick || $[234] !== searchProperties || $[235] !== t100 || $[236] !== t102 || $[237] !== t103 || $[238] !== t41 || $[239] !== t42 || $[240] !== t51 || $[241] !== t63 || $[242] !== t64 || $[243] !== t81 || $[244] !== t90) {
    t104 = {
      headerProps: t39 as HeaderPropertiesType,
      searchProperties,
      citiesFilterProperties: t41,
      modalProps: t42,
      listingContainerProps: t51,
      hotelListHeaderProps: t63,
      hotelPaginationProps: t64,
      hotelCitiesMainContentProps: t81,
      hotelBreadcrumbsProperties: t90,
      telesaleProperties: t100,
      bannersProperties: t102,
      onMapClick,
      hasMap: t103,
      isNewPlP
    };
    $[232] = isNewPlP;
    $[233] = onMapClick;
    $[234] = searchProperties;
    $[235] = t100;
    $[236] = t102;
    $[237] = t103;
    $[238] = t41;
    $[239] = t42;
    $[240] = t51;
    $[241] = t63;
    $[242] = t64;
    $[243] = t81;
    $[244] = t90;
    $[245] = t104;
  } else {
    t104 = $[245];
  }
  return t104;
};
function _temp() {
  return fetchInstalmentWallet();
}
function _temp2(t0) {
  const {
    refetch
  } = t0;
  return refetch();
}
function _temp3(t0) {
  const {
    isFetching,
    status
  } = t0;
  return isFetching || status === "pending";
}
function _temp4(t0) {
  const {
    data
  } = t0;
  return data ? [data] : [];
}
function _temp5(t0) {
  const {
    data: data_0
  } = t0;
  return (data_0 || []) as Array<VillaLocationResponseType>;
}
function _temp6(t0) {
  const {
    current_page
  } = t0;
  return current_page;
}
function _temp7(t0) {
  const {
    room_data
  } = t0;
  return room_data?.is_active;
}
function _temp8(t0) {
  const {
    room_data: room_data_0
  } = t0;
  return room_data_0.id;
}
function _temp9(t0) {
  const {
    queryKey
  } = t0;
  return fetchHotelRateData(queryKey[1]);
}
function _temp10(t0) {
  const {
    queryKey: queryKey_0
  } = t0;
  return fetchHotelReviewData(queryKey_0[1], 1, "villa", {} as Record<string, string>);
}
function _temp11() {
  return <AccommodationCardSkeleton />;
}
function _temp12(item_1) {
  return item_1.id;
}
function _temp13(comment) {
  return comment;
}